import React, { MouseEvent, useCallback, useRef, useState } from "react";

import DoubleChatBubbleIcon from "ac-components/ac-icons/DoubleChatBubbleIcon";
import ACPopover from "ac-components/ac-popover/ACPopover";

import CommentsOverlay from "./CommentsOverlay";
import "./FindingCommentsCell.less";

declare global {
  interface Window {
    __scrollToFindingComments?: boolean;
  }
}

interface FindingCommentsCellProps {
  commentsCount: number | undefined;
  findingId: number;
  showFindingDetail?: (e: MouseEvent<HTMLAnchorElement>) => void;
  index?: number;
}

export default function FindingCommentsCell({
  commentsCount,
  findingId,
  showFindingDetail,
  index,
}: FindingCommentsCellProps) {
  const cellRef = useRef<HTMLSpanElement>(null);
  const [popoverVisible, setPopoverVisible] = useState(false);

  const openFinding = useCallback(() => {
    if (!showFindingDetail || !cellRef.current) return;
    setPopoverVisible(false);
    cellRef.current.dataset.id = String(findingId);
    cellRef.current.dataset.index = String(index ?? 0);
    showFindingDetail({
      currentTarget: cellRef.current,
    } as unknown as MouseEvent<HTMLAnchorElement>);
    // Set scroll flag after calling showFindingDetail
    // Auto-clear after 10s in case the modal didn't actually open (e.g. noop handler)
    window.__scrollToFindingComments = true;
    setTimeout(() => {
      window.__scrollToFindingComments = false;
    }, 10000);
  }, [findingId, showFindingDetail, index]);

  const handleClick = useCallback(() => {
    if (!commentsCount || commentsCount === 0) return;
    openFinding();
  }, [commentsCount, openFinding]);

  if (commentsCount == null || commentsCount === 0) {
    return (
      <span className="finding-comments-cell finding-comments-cell--empty">
        <DoubleChatBubbleIcon className="finding-comments-cell__icon" />
        <span className="finding-comments-cell__count">-</span>
      </span>
    );
  }

  return (
    <ACPopover
      trigger="hover"
      placement="right"
      offsetX={8}
      popupVisible={popoverVisible}
      onPopupVisibleChange={setPopoverVisible}
      className="comments-popover"
      content={
        popoverVisible ? (
          <CommentsOverlay
            findingId={findingId}
            onViewFinding={showFindingDetail ? openFinding : undefined}
          />
        ) : null
      }
    >
      <span
        ref={cellRef}
        className={`finding-comments-cell${showFindingDetail ? " finding-comments-cell--clickable" : ""}`}
        onClick={handleClick}
      >
        <DoubleChatBubbleIcon className="finding-comments-cell__icon" />
        <span className="finding-comments-cell__count">{commentsCount}</span>
      </span>
    </ACPopover>
  );
}
